import type { Middleware } from "@reduxjs/toolkit";
import { isFulfilled } from "@reduxjs/toolkit";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import {
  clearSystemNotificationBanner,
  setSystemNotificationBanner,
} from "_shared/_providers/banner/system-notification-banner-provider";
import { AlertType } from "_shared/_views/system-notification-banner/system-notification-banner";
import Session from "users/__session/session";

export const api = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: AttorneyHubAPIService.apiBaseUrl,
    prepareHeaders: (headers) => {
      const authToken = Session.loadFromStorage(() => {}).authToken?.value;
      if (authToken) {
        headers.set("Authorization", `Bearer ${authToken}`);
      }
      return headers;
    },
    fetchFn: async (input, init) => {
      if (input instanceof Request && input.method.toUpperCase() === "PATCH") {
        input.headers.set("Content-Type", "application/json-patch+json");
      }

      if (init?.method?.toUpperCase() === "PATCH") {
        init.headers ??= {
          "Content-Type": "application/json-patch+json",
        };
        if (init.headers instanceof Headers) {
          init.headers.set("Content-Type", "application/json-patch+json");
        }
      }
      return fetch(input, init);
    },
  }),
  tagTypes: [
    "AdministratorInvitation",
    "Bookmark",
    "Company",
    "EntityMember",
    "FeeSchedule",
    "Individual",
    "NetworkedGroup",
    "NetworkInvitation",
    "Proposal",
    "Team",
    "UserCoworker",
    "UserInformation",
    "UserNetworkConnection",
    "Marketplace",
    "WorkAgreement"
  ],
  endpoints: () => ({}),
});

function getErrorStatus(action: { error: unknown }): number {
  if ("status" in action) {
    return typeof action.status === "number" ? action.status : 0;
  }

  let payload: { status: unknown };
  if (
    !("payload" in action) ||
    typeof (action as { payload: unknown }).payload !== "object" ||
    (action as { payload: object }).payload === null
  ) {
    return 0;
  }

  payload = action.payload as { status: unknown };
  if ("status" in payload) {
    return typeof payload.status === "number" ? payload.status : 0;
  }

  let error: { status: unknown };
  if (
    "error" in payload &&
    typeof (payload as { error: unknown }).error === "object" &&
    (payload as { error: object }).error !== null
  ) {
    error = (payload as { error: { status: unknown } }).error;
    if ("status" in error) {
      return typeof error.status === "number" ? error.status : 0;
    }
  }

  return 0;
}

export const apiServerDownCheck: Middleware = () => (next) => (action) => {
  const result = next(action);
  if (isFulfilled(action)) {
    clearSystemNotificationBanner();
    return result;
  }
  if (typeof action !== "object" || action === null) {
    return result;
  }
  if (!("error" in action)) {
    return result;
  }
  if (isAbortedError(action.error)) {
    return result;
  }

  const errorStatus = getErrorStatus(action);
  if (!errorStatus) {
    setSystemNotificationBanner(
      AlertType.Error,
      "AttorneyHub is currently unavailable. Please check your internet connection and try again later.",
    );
  }

  return result;
};
function isAbortedError(error: unknown): error is { message: string } {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    (error as { message: string }).message.toLowerCase().includes("aborted")
  );
}
